import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * TaskManagerHeader component - Header with stats and navigation
 */
const TaskManagerHeader = ({ stats, currentFilter, onFilterChange }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
            <CheckCircle2 className="text-white" size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Task Manager</h1>
            <p className="text-blue-100 text-sm mt-0.5">Track and manage your improvement tasks</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/gap-analysis')}
            className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition font-medium border border-white/30"
          >
            ← Business Assessment
          </button>
          <button
            onClick={() => navigate('/personal-gap-analysis')}
            className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition font-medium border border-white/30"
          >
            ← Personal Assessment
          </button>
        </div>
      </div>

      {/* Inline Compact Stats - High Contrast */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div
            onClick={() => onFilterChange('all')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-white/40 hover:border-white/70 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'all' ? 'border-white/70 ring-2 ring-white' : 'border-white/40'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Total</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.total}</p>
          </div>
          <div
            onClick={() => onFilterChange('not_started')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-red-500/80 hover:border-red-300 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'not_started' ? 'bg-red-500/60 border-red-300 ring-2 ring-red-400' : 'border-red-200/50'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Not Started</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.not_started}</p>
          </div>
          <div
            onClick={() => onFilterChange('in_progress')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-blue-500/80 hover:border-blue-300 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'in_progress' ? 'bg-blue-500/60 border-blue-300 ring-2 ring-blue-400' : 'border-blue-200/50'}`}
          >
            <p className="text-xs text-white font-bold mb-1">In Progress</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.in_progress}</p>
          </div>
          <div
            onClick={() => onFilterChange('under_review')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-purple-500/80 hover:border-purple-300 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'under_review' ? 'bg-purple-500/60 border-purple-300 ring-2 ring-purple-400' : 'border-purple-200/50'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Under Review</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.under_review}</p>
          </div>
          <div
            onClick={() => onFilterChange('completed')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-green-500/80 hover:border-green-300 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'completed' ? 'bg-green-500/60 border-green-300 ring-2 ring-green-400' : 'border-green-200/50'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Completed</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.completed}</p>
          </div>
          <div
            onClick={() => onFilterChange('not_relevant')}
            className={`bg-white/25 backdrop-blur-md rounded-lg p-3 border-2 shadow-lg hover:bg-gray-500/80 hover:border-gray-300 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'not_relevant' ? 'bg-gray-500/60 border-gray-300 ring-2 ring-gray-400' : 'border-gray-200/50'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Not Relevant</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.not_relevant}</p>
          </div>
          <div
            onClick={() => onFilterChange('all')}
            className={`bg-white/30 backdrop-blur-md rounded-lg p-3 border-2 shadow-xl hover:bg-white/50 hover:border-white/80 hover:scale-105 transition-all duration-200 cursor-pointer ${currentFilter === 'all' ? 'border-white/80 ring-2 ring-white' : 'border-white/60'}`}
          >
            <p className="text-xs text-white font-bold mb-1">Completion</p>
            <p className="text-2xl font-extrabold text-white drop-shadow-md">{stats.completion_rate}%</p>
            <p className="text-xs text-white font-semibold">{stats.completed}/{stats.relevant_tasks}</p>
          </div>
        </div>
      )}
    </div>
  );
};

TaskManagerHeader.propTypes = {
  stats: PropTypes.shape({
    total: PropTypes.number.isRequired,
    not_started: PropTypes.number.isRequired,
    in_progress: PropTypes.number.isRequired,
    under_review: PropTypes.number.isRequired,
    completed: PropTypes.number.isRequired,
    not_relevant: PropTypes.number.isRequired,
    completion_rate: PropTypes.number.isRequired,
    relevant_tasks: PropTypes.number.isRequired,
  }),
  currentFilter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

export default TaskManagerHeader;
